import { MediaItemSource } from "services/KnowfullyAdminClient";
import { DeliveryMethodId, DeliveryMethodSettingCodeConstants } from "types/common-types";
import { findSingleHtmlFile } from "./zipHelper";
import { extractVimeoId } from "./vimeoHelper";

/**
 * Delivery Method Helper utility for generating access URLs for different delivery methods
 */
export class DeliveryMethodHelper {
  static async GetDeliveryMethodAccessUrl(
    baseUrl: string,
    file: File,
    source: MediaItemSource
  ): Promise<string> {
    switch (source) {
      case MediaItemSource.ArticulateRise:
        return DeliveryMethodHelper.GetArticulateRiseAccessUrl(baseUrl);

      case MediaItemSource.ISpring:
        return DeliveryMethodHelper.GetISpringAccessUrl(baseUrl);

      case MediaItemSource.InfographEd:
        return await DeliveryMethodHelper.GetInfographEdAccessUrl(
          baseUrl,
          file
        );

      default:
        return baseUrl;
    }
  }

  /**
   * Gets ArticulateRise delivery method access URL
   * Converts: https://storage.blob.core.windows.net/container/file.zip
   * To: https://storage.blob.core.windows.net/container/file/content/index.html
   */
  private static GetArticulateRiseAccessUrl(baseUrl: string): string {
    if (!baseUrl.endsWith(".zip")) {
      throw new Error("The uploaded file must be a ZIP archive");
    }

    const lastSlashIndex = baseUrl.lastIndexOf("/");
    const baseUrlWithoutZip = decodeURIComponent(
      baseUrl.substring(0, lastSlashIndex)
    );
    return `${baseUrlWithoutZip}/content/index.html`;
  }

  /**
   * Gets iSpring delivery method access URL
   * Converts: https://storage.blob.core.windows.net/container/file.zip
   * To: https://storage.blob.core.windows.net/container/file/res/index.html
   */
  private static GetISpringAccessUrl(baseUrl: string): string {
    if (!baseUrl.endsWith(".zip")) {
      throw new Error("The uploaded file must be a ZIP archive");
    }

    const lastSlashIndex = baseUrl.lastIndexOf("/");
    const baseUrlWithoutZip = decodeURIComponent(
      baseUrl.substring(0, lastSlashIndex)
    );
    return `${baseUrlWithoutZip}/res/index.html`;
  }

  /**
   * Gets InfographEd delivery method access URL
   * Analyzes the ZIP to find the HTML file and constructs the appropriate URL
   * Converts: https://storage.blob.core.windows.net/container/file.zip
   * To: https://storage.blob.core.windows.net/container/file/index.html
   */
  private static async GetInfographEdAccessUrl(
    baseUrl: string,
    file: File
  ): Promise<string> {
    if (!baseUrl.endsWith(".zip")) {
      throw new Error("The uploaded file must be a ZIP archive");
    }

    const htmlPath = await findSingleHtmlFile(file);
    if (!htmlPath) {
      throw new Error("ZIP file must contain at least one HTML file");
    }

    const lastSlashIndex = baseUrl.lastIndexOf("/");
    const baseUrlWithoutZip = decodeURIComponent(
      baseUrl.substring(0, lastSlashIndex)
    );
    return `${baseUrlWithoutZip}/${htmlPath}`;
  }

  static processSettingValues(
    deliveryMethodId: string | undefined,
    settingValues: any[] | undefined
  ): any[] | undefined {
    if (!settingValues || !deliveryMethodId) return settingValues;

    // Only process Vimeo delivery methods (for now)
    if (deliveryMethodId !== DeliveryMethodId.Vimeo) {
      return settingValues;
    }

    return settingValues.map((sv) => {
      if (sv.setting?.name === DeliveryMethodSettingCodeConstants.Id) {
        return {
          ...sv,
          value: extractVimeoId(sv.value?.toString() || ""),
        };
      }

      return sv;
    });
  }
}
